import React, { useMemo, useState } from 'react';
import { useCRM } from '../../utils/CRMContext';
import { useActivities } from '../../hooks/useActivities';
import { 
  ReportConfig, 
  DateRangePreset,
  Lead,
  EnhancedDeal,
  Activity,
  TimberProject 
} from '../../types';
import { exportToCSV, exportToPDF } from '../../utils/reportExport';
import './ReportPreview.css';

interface ReportPreviewProps {
  config: ReportConfig;
  onBack: () => void;
}

export const ReportPreview: React.FC<ReportPreviewProps> = ({ config, onBack }) => {
  const { activities } = useActivities();
  const { leads, enhancedDeals, timberProjects, pipelines } = useCRM();
  const [exporting, setExporting] = useState<'csv' | 'pdf' | null>(null);

  // Get date range
  const getDateRange = (): { start: Date; end: Date } => {
    if (typeof config.dateRange === 'object' && config.dateRange) {
      return config.dateRange;
    }
    
    const now = new Date();
    const preset = (config.dateRange as DateRangePreset) || 'all-time';
    
    switch (preset) {
      case 'today':
        return { start: new Date(now.setHours(0, 0, 0, 0)), end: new Date() };
      case 'yesterday': {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return { start: new Date(yesterday.setHours(0, 0, 0, 0)), end: new Date(yesterday.setHours(23, 59, 59)) };
      }
      case 'this-week': {
        const start = new Date(now);
        start.setDate(start.getDate() - start.getDay());
        return { start: new Date(start.setHours(0, 0, 0, 0)), end: new Date() };
      }
      case 'last-week': {
        const start = new Date(now);
        start.setDate(start.getDate() - start.getDay() - 7);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        return { start: new Date(start.setHours(0, 0, 0, 0)), end: new Date(end.setHours(23, 59, 59)) };
      }
      case 'this-month':
        return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: new Date() };
      case 'last-month': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        return { start, end };
      }
      case 'this-quarter': {
        const quarter = Math.floor(now.getMonth() / 3);
        return { start: new Date(now.getFullYear(), quarter * 3, 1), end: new Date() };
      }
      case 'last-quarter': {
        const quarter = Math.floor(now.getMonth() / 3) - 1;
        const start = new Date(now.getFullYear(), quarter * 3, 1);
        const end = new Date(now.getFullYear(), (quarter + 1) * 3, 0);
        return { start, end };
      }
      case 'this-year':
        return { start: new Date(now.getFullYear(), 0, 1), end: new Date() };
      case 'last-year':
        return { start: new Date(now.getFullYear() - 1, 0, 1), end: new Date(now.getFullYear() - 1, 11, 31) };
      case 'all-time':
      default:
        return { start: new Date(2000, 0, 1), end: new Date() };
    }
  };

  // Filter and transform data based on config
  const reportData = useMemo(() => {
    const dateRange = getDateRange();
    let data: any[] = [];

    // Get raw data based on source
    switch (config.dataSource) {
      case 'leads':
        data = leads.map(lead => ({
          ...lead,
          createdAt: new Date(lead.createdAt),
        }));
        break;
      case 'deals':
        data = enhancedDeals.map(deal => {
          const pipeline = pipelines.find(p => p.id === deal.pipelineId);
          const stage = pipeline?.stages.find(s => s.id === deal.stageId);
          const daysInStage = Math.floor(
            (Date.now() - new Date(deal.currentStageEnteredAt).getTime()) / (1000 * 60 * 60 * 24)
          );
          return {
            ...deal,
            stageId: stage?.name || deal.stageId,
            daysInStage,
            createdAt: new Date(deal.createdAt),
            closedAt: deal.closedAt ? new Date(deal.closedAt) : null,
          };
        });
        break;
      case 'activities':
        data = activities.map(activity => ({
          ...activity,
          createdAt: new Date(activity.createdAt),
          scheduledAt: activity.scheduledAt ? new Date(activity.scheduledAt) : null,
          completedAt: activity.completedAt ? new Date(activity.completedAt) : null,
        }));
        break;
      case 'projects':
        data = timberProjects.map(project => ({
          ...project,
          createdAt: new Date(project.createdAt),
          startDate: new Date(project.startDate),
          targetEndDate: new Date(project.targetEndDate),
        }));
        break;
      case 'conversion-metrics':
        // Aggregate conversion metrics by source
        const sourceMap = new Map<string, any>();
        leads.forEach(lead => {
          const source = lead.source || 'unspecified';
          if (!sourceMap.has(source)) {
            sourceMap.set(source, { source, leadCount: 0, dealCount: 0, wonCount: 0, totalValue: 0 });
          }
          sourceMap.get(source)!.leadCount++;
        });
        enhancedDeals.forEach(deal => {
          const source = deal.source || 'unspecified';
          if (!sourceMap.has(source)) {
            sourceMap.set(source, { source, leadCount: 0, dealCount: 0, wonCount: 0, totalValue: 0 });
          }
          const entry = sourceMap.get(source)!;
          entry.dealCount++;
          if (deal.outcome === 'won') {
            entry.wonCount++;
            entry.totalValue += deal.value;
          }
        });
        data = Array.from(sourceMap.values()).map(entry => ({
          ...entry,
          conversionRate: entry.leadCount > 0 ? (entry.dealCount / entry.leadCount) * 100 : 0,
          winRate: entry.dealCount > 0 ? (entry.wonCount / entry.dealCount) * 100 : 0,
          avgValue: entry.wonCount > 0 ? entry.totalValue / entry.wonCount : 0,
          createdAt: new Date(), // For date filtering
        }));
        break;
      default:
        data = [];
    }

    // Filter by date range
    data = data.filter(item => {
      const itemDate = new Date(item.createdAt);
      return itemDate >= dateRange.start && itemDate <= dateRange.end;
    });

    // Apply custom filters
    config.filters.forEach(filter => {
      data = data.filter(item => {
        const value = item[filter.field];
        const filterValue = filter.value;

        switch (filter.operator) {
          case 'equals':
            return String(value).toLowerCase() === String(filterValue).toLowerCase();
          case 'not-equals':
            return String(value).toLowerCase() !== String(filterValue).toLowerCase();
          case 'contains':
            return String(value).toLowerCase().includes(String(filterValue).toLowerCase());
          case 'greater-than':
            return Number(value) > Number(filterValue);
          case 'less-than':
            return Number(value) < Number(filterValue);
          default:
            return true;
        }
      });
    });

    // Sort
    if (config.sortBy && config.sortBy.length > 0) {
      const { column, direction } = config.sortBy[0];
      data.sort((a, b) => {
        const aVal = a[column];
        const bVal = b[column];
        const comparison = typeof aVal === 'number' 
          ? aVal - bVal 
          : String(aVal).localeCompare(String(bVal));
        return direction === 'desc' ? -comparison : comparison;
      });
    }

    return data;
  }, [config, leads, enhancedDeals, activities, timberProjects, pipelines]);

  // Group data if needed
  const groupedData = useMemo(() => {
    if (!config.groupBy || config.groupBy.length === 0) {
      return { '': reportData };
    }

    const groupField = config.groupBy[0];
    const groups: Record<string, any[]> = {};
    
    reportData.forEach(item => {
      const groupKey = String(item[groupField] || 'Unknown');
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
    });

    return groups;
  }, [reportData, config.groupBy]);

  // Calculate summary
  const summary = useMemo(() => {
    const currencyColumns = config.columns.filter(c => c.type === 'currency');
    const numberColumns = config.columns.filter(c => c.type === 'number');
    
    const totals: Record<string, number> = {};
    
    currencyColumns.forEach(col => {
      totals[col.field] = reportData.reduce((sum, item) => sum + (Number(item[col.field]) || 0), 0);
    });
    
    numberColumns.forEach(col => {
      totals[col.field] = reportData.reduce((sum, item) => sum + (Number(item[col.field]) || 0), 0);
    });
    
    return {
      totalRows: reportData.length,
      totals,
    };
  }, [reportData, config.columns]);

  const formatValue = (value: any, type: string): string => {
    if (value === null || value === undefined) return '-';
    
    switch (type) {
      case 'currency':
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
      case 'percentage':
        return `${Number(value).toFixed(1)}%`;
      case 'date':
        return value instanceof Date 
          ? value.toLocaleDateString() 
          : new Date(value).toLocaleDateString();
      case 'number':
        return Number(value).toLocaleString();
      default:
        return String(value);
    }
  };

  const handleExportCSV = async () => {
    setExporting('csv');
    try {
      await exportToCSV({
        filename: `${config.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`,
        columns: config.columns,
        data: reportData,
      });
    } catch (err) {
      console.error('Error exporting CSV:', err);
    } finally {
      setExporting(null);
    }
  };

  const handleExportPDF = async () => {
    setExporting('pdf');
    try {
      await exportToPDF({
        filename: `${config.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
        title: config.name,
        columns: config.columns,
        data: reportData,
        summary,
        filters: config.filters,
        dateRange: config.dateRange,
      });
    } catch (err) {
      console.error('Error exporting PDF:', err);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="report-preview">
      {/* Preview Header */}
      <div className="preview-header">
        <div className="header-left">
          <button className="back-btn" onClick={onBack}>← Back to Builder</button>
          <h2>{config.name}</h2>
        </div>
        <div className="header-right">
          <span className="row-count">{summary.totalRows} records</span>
          <button 
            className="export-btn csv"
            onClick={handleExportCSV}
            disabled={exporting !== null}
          >
            {exporting === 'csv' ? 'Exporting...' : 'Export CSV'}
          </button>
          <button 
            className="export-btn pdf"
            onClick={handleExportPDF}
            disabled={exporting !== null}
          >
            {exporting === 'pdf' ? 'Exporting...' : 'Export PDF'}
          </button>
        </div>
      </div>

      {/* Applied Filters */}
      {(config.filters.length > 0 || config.dateRange) && (
        <div className="applied-filters">
          <span className="filters-label">Filters:</span>
          {config.dateRange && (
            <span className="filter-tag">
              Date: {typeof config.dateRange === 'string' ? config.dateRange : 'Custom'}
            </span>
          )}
          {config.filters.map((filter, index) => (
            <span key={index} className="filter-tag">
              {filter.field} {filter.operator} "{filter.value}"
            </span>
          ))}
        </div>
      )}

      {/* Data Table */}
      <div className="preview-table-container">
        {Object.entries(groupedData).map(([groupName, items]) => (
          <div key={groupName} className="table-group">
            {groupName && config.groupBy && config.groupBy.length > 0 && (
              <div className="group-header">
                <span className="group-name">{groupName}</span>
                <span className="group-count">{items.length} items</span>
              </div>
            )}
            
            <table className="preview-table">
              <thead>
                <tr>
                  {config.columns.map(col => (
                    <th key={col.field}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={index}>
                    {config.columns.map(col => (
                      <td key={col.field} className={`type-${col.type}`}>
                        {formatValue(item[col.field], col.type)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        {reportData.length === 0 && (
          <div className="no-data">
            <p>No data matches your report criteria</p>
          </div>
        )}
      </div>

      {/* Summary */}
      {reportData.length > 0 && Object.keys(summary.totals).length > 0 && (
        <div className="preview-summary">
          <h3>Summary</h3>
          <div className="summary-grid">
            <div className="summary-item">
              <span className="summary-label">Total Records</span>
              <span className="summary-value">{summary.totalRows}</span>
            </div>
            {Object.entries(summary.totals).map(([field, total]) => {
              const col = config.columns.find(c => c.field === field);
              return (
                <div key={field} className="summary-item">
                  <span className="summary-label">Total {col?.label || field}</span>
                  <span className="summary-value">{formatValue(total, col?.type || 'number')}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportPreview;
